import React from 'react';
import ThemeProvider from '@via-profit/ui-kit/ThemeProvider';

import MapView, { type MapViewHandle } from './components/Map/MapView';
import SearchBox from './components/SearchBox/SearchBox';
import Toolbar, { type Mode } from './components/Toolbar/Toolbar';
import AddressCard from './components/AddressCard/AddressCard';
import MapControls from './components/MapControls/MapControls';
import SidePanel from './components/SidePanel/SidePanel';
import RoutePanel, { type RoutePointPreset } from './components/RoutePanel/RoutePanel';
import PlacePopup from './components/PlacePopup/PlacePopup';
import GlobalStyles from './GlobalStyles';
import { fetchGeometry, reportNoGeo, selectResult, type SearchArea } from './api/geocoder';
import { barePlace, resolvePlace, type MapHit, type Place } from './api/place';
import type { Route } from './api/routing';
import type { GeocodeResult } from './api/types';
import { config } from './config';
import { darkTheme, lightTheme } from './theme';
import { isMobile } from './layout';
import { initialState, saveState, type MapViewState, type RouteUrlState } from './urlState';


type ColorTheme = 'light' | 'dark';
const THEME_KEY = 'gar-map:theme';

/** Выбранная тема, а если не выбирали - как в системе. */
function initialTheme(): ColorTheme {
  try {
    const saved = window.localStorage.getItem(THEME_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    // localStorage недоступен - как в системе
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}


// Для этих типов результатов подсвечиваем на карте контур объекта вместо
// одной точки. Зум - запасной вариант, если контура в базе нет.
const AREA_ZOOM: Partial<Record<GeocodeResult['precision'], number>> = {
  region: 7,
  area: 10,
  city: 12,
  locality: 13,
  street: 16,
};



const App: React.FC = () => {
  const [colorTheme, setColorTheme] = React.useState<ColorTheme>(initialTheme);
  React.useEffect(() => {
    document.documentElement.dataset.theme = colorTheme;
  }, [colorTheme]);
  const toggleTheme = React.useCallback(() => {
    setColorTheme(current => {
      const next = current === 'dark' ? 'light' : 'dark';
      try {
        window.localStorage.setItem(THEME_KEY, next);
      } catch {
        // не запомнится - не страшно
      }
      return next;
    });
  }, []);

  const mapRef = React.useRef<MapViewHandle>(null);
  // ссылка или прошлый раз - читается один раз при открытии
  const [initial] = React.useState(initialState);
  const [mode, setMode] = React.useState<Mode>(initial.mode ?? 'search');
  const [view, setView] = React.useState<MapViewState | null>(initial.view ?? null);
  // последнее состояние панели маршрута: для ссылки и чтобы при возврате на
  // вкладку маршрут остался
  const [routeState, setRouteState] = React.useState<RouteUrlState | null>(initial.route ?? null);
  // читается в обработчике клика по карте, который создаётся один раз
  const modeRef = React.useRef(mode);
  modeRef.current = mode;
  const [selected, setSelected] = React.useState<GeocodeResult | null>(null);
  const [selectedLogId, setSelectedLogId] = React.useState<string | null>(null);
  // подсказка по клику на карте: place === null - адрес ещё определяется
  const [clicked, setClicked] = React.useState<{
    lat: number;
    lon: number;
    place: Place | null;
    /** куда кликнули и во что попали - для ссылки (адрес по ней ищется заново) */
    origin: { lat: number; lon: number; kind: 'building' | 'street' | 'other' };
  } | null>(null);
  // точка для панели маршрута (кнопка "Маршрут" - в "Куда", выбор на карте -
  // в поле, у которого нажали кнопку)
  const [routePreset, setRoutePreset] = React.useState<RoutePointPreset | null>(null);
  const [pick, setPick] = React.useState<{ target: string; state: 'picking' | 'resolving' } | null>(null);
  const pickRef = React.useRef(pick);
  pickRef.current = pick;
  // что пользователь сообщил о выбранном варианте: 'confirmed' - адрес верный,
  // 'no_geo' - адрес верный, но координат нет. Повторный выбор перезаписывает
  // предыдущий и на бэкенде, так что промах кнопкой можно исправить.
  const [feedback, setFeedback] = React.useState<'confirmed' | 'no_geo' | null>(null);
  const geometryAbortRef = React.useRef<AbortController | null>(null);
  const placeAbortRef = React.useRef<AbortController | null>(null);
  const [mapCenter, setMapCenter] = React.useState<SearchArea>({
    lat: initial.view?.lat ?? config.initialView.lat,
    lon: initial.view?.lon ?? config.initialView.lng,
  });

  const handleMapMoveEnd = React.useCallback(
    (
      lat: number,
      lon: number,
      bbox: [number, number, number, number],
      { zoom, bearing, pitch }: { zoom: number; bearing: number; pitch: number },
    ) => {
      setMapCenter({ lat, lon, bbox });
      setView({ lat, lon, zoom, bearing, pitch });
    },
    [],
  );

  const handleSelect = React.useCallback((result: GeocodeResult, logId: string | null) => {
    setSelected(result);
    setSelectedLogId(logId);
    setFeedback(null);

    geometryAbortRef.current?.abort();
    placeAbortRef.current?.abort();
    setClicked(null);
    mapRef.current?.setHighlight(null);

    const hasPoint = result.latitude != null && result.longitude != null;
    const areaZoom = AREA_ZOOM[result.precision];
    const target = result.hierarchy[result.hierarchy.length - 1];

    if (areaZoom == null || !target) {
      if (!hasPoint) mapRef.current?.clearMarker();
      if (hasPoint) {
        mapRef.current?.flyTo(result.latitude!, result.longitude!, 16);
        mapRef.current?.setMarker(result.latitude!, result.longitude!);
      }
      return;
    }

    // Центр и административная граница показываются одновременно.
    if (hasPoint) mapRef.current?.setMarker(result.latitude!, result.longitude!);
    else mapRef.current?.clearMarker();
    const controller = new AbortController();
    geometryAbortRef.current = controller;

    fetchGeometry(target.ao_id, controller.signal)
      .catch((error: unknown) => {
        if ((error as Error).name === 'AbortError') throw error;
        console.error('Geometry fetch failed:', error);
        return null;
      })
      .then(feature => {
        if (feature) {
          mapRef.current?.setHighlight(feature.geometry);
          mapRef.current?.fitBounds(feature.bbox);
        } else if (hasPoint) {
          mapRef.current?.flyTo(result.latitude!, result.longitude!, areaZoom);
          mapRef.current?.setMarker(result.latitude!, result.longitude!);
        }
      })
      .catch(() => {
        // запрос отменён более новым выбором - ничего не делаем
      });
  }, []);

  const handleConfirm = React.useCallback(() => {
    if (!selected || !selectedLogId) return;
    selectResult(selectedLogId, selected.id);
    setFeedback('confirmed');
  }, [selected, selectedLogId]);

  const handleNoGeo = React.useCallback(() => {
    if (!selected || !selectedLogId) return;
    reportNoGeo(selectedLogId, selected.id);
    setFeedback('no_geo');
  }, [selected, selectedLogId]);

  const handleModeChange = React.useCallback((next: Mode) => {
    setMode(next);
    // у каждого режима свои объекты на карте - чужие убираем
    geometryAbortRef.current?.abort();
    placeAbortRef.current?.abort();
    setClicked(null);
    setRoutePreset(null);
    setPick(null);
    mapRef.current?.setHighlight(null);
    mapRef.current?.clearMarker();
    mapRef.current?.setRoute(null);
    mapRef.current?.setRoutePoints([]);
    setSelected(null);
    setSelectedLogId(null);
    setFeedback(null);
  }, []);

  /** Кнопка "Маршрут": точка уходит в поле "Куда" панели маршрута. */
  const handleRouteTo = React.useCallback(
    (endpoint: GeocodeResult, label: string) => {
      handleModeChange('route');
      setRoutePreset({ target: 'last', result: endpoint, label });
    },
    [handleModeChange],
  );

  const handlePopupClose = React.useCallback(() => {
    placeAbortRef.current?.abort();
    setClicked(null);
    mapRef.current?.clearMarker();
    mapRef.current?.setHighlight(null);
  }, []);

  /** Кнопка выбора на карте у поля target: повторное нажатие (или нажатие
   *  у того же поля) отменяет выбор. */
  const handlePickToggle = React.useCallback((target: string) => {
    placeAbortRef.current?.abort();
    setPick(current => (current?.target === target ? null : { target, state: 'picking' }));
  }, []);

  const isPicking = pick?.state === 'picking';
  React.useEffect(() => {
    if (!isPicking) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPick(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPicking]);

  // предыдущий маршрут - чтобы подгонять карту только под новую геометрию,
  // а не дёргать её при каждом пересчёте с тем же результатом
  const shownRouteRef = React.useRef<Route | null>(null);

  const handleRouteChange = React.useCallback(
    (route: Route | null, points: ([number, number] | null)[], others: readonly Route[]) => {
      mapRef.current?.setRoute(
        route?.geometry ?? null,
        route?.tollSections.map(section => section.geometry),
        others.map(other => other.geometry),
      );
      mapRef.current?.setRoutePoints(points);

      if (route && route !== shownRouteRef.current) {
        // слева панель маршрута (380px + отступы) - маршрут вписываем правее
        // неё; на телефоне панель сверху (свёрнутая - ~130px)
        const padding = isMobile()
          ? { top: 150, right: 30, bottom: 50, left: 30 }
          : { top: 60, right: 60, bottom: 60, left: window.innerWidth > 800 ? 420 : 40 };
        mapRef.current?.fitBounds(route.bbox, undefined, padding);
      } else if (!route) {
        // одна точка выбрана, маршрута ещё нет - показываем её
        const set = points.filter((p): p is [number, number] => p != null);
        if (set.length === 1) mapRef.current?.showPoint(set[0][0], set[0][1], 12);
      }
      shownRouteRef.current = route;
    },
    [],
  );

  const handleMapClick = React.useCallback((lat: number, lon: number, hit: MapHit) => {
    const resolve = () => {
      placeAbortRef.current?.abort();
      const controller = new AbortController();
      placeAbortRef.current = controller;
      return resolvePlace(lat, lon, hit, controller.signal).catch((error: unknown) => {
        // отменён более новым кликом/выбором - тот сам управляет состоянием
        if ((error as Error).name === 'AbortError') throw error;
        console.error('Reverse geocode failed:', error);
        return barePlace(lat, lon);
      });
    };

    if (modeRef.current === 'route') {
      // в режиме маршрута клик по карте только задаёт точку, у которой нажали
      // кнопку выбора на карте
      const current = pickRef.current;
      if (current?.state !== 'picking') return;
      const { target } = current;
      setPick({ target, state: 'resolving' });
      resolve()
        .then(place => {
          setRoutePreset({ target, result: place.endpoint, label: place.routeLabel ?? place.title });
          setPick(null);
        })
        .catch(() => {});
      return;
    }

    geometryAbortRef.current?.abort();
    setSelected(null);
    setSelectedLogId(null);
    setFeedback(null);
    // пока адрес определяется - маркер и подсказка в точке клика, затем
    // переезжают на дом, который нашёл геокодер. Контур по клику не
    // подсвечиваем - только у выбранного в поиске
    mapRef.current?.setHighlight(null);
    mapRef.current?.setMarker(lat, lon);
    // подъезд по ссылке не восстановить (его номер - в тайлах) - ссылка на его дом
    const origin =
      hit.kind === 'entrance'
        ? { lat: hit.house?.[0] ?? lat, lon: hit.house?.[1] ?? lon, kind: 'building' as const }
        : { lat, lon, kind: hit.kind };
    setClicked({ lat, lon, place: null, origin });

    resolve()
      .then(place => {
        mapRef.current?.setMarker(place.marker[0], place.marker[1]);
        setClicked({ lat: place.marker[0], lon: place.marker[1], place, origin });
      })
      .catch(() => {});
  }, []);

  // выбранный дом - клик по дому/подъезду или дом из поиска: на карте его
  // подъезды и стрелки ко входам
  const buildingPoint: [number, number] | null =
    clicked?.origin.kind === 'building'
      ? [clicked.origin.lat, clicked.origin.lon]
      : selected?.precision === 'house' && selected.latitude != null && selected.longitude != null
        ? [selected.latitude, selected.longitude]
        : null;
  const buildingKey = buildingPoint?.join(',') ?? null;
  React.useEffect(() => {
    mapRef.current?.selectBuilding(buildingKey ? (buildingKey.split(',').map(Number) as [number, number]) : null);
  }, [buildingKey]);

  // всё, что на экране, - в адрес страницы и в localStorage (с задержкой:
  // при перетаскивании карты moveend приходит часто)
  const clickedOrigin = clicked?.origin ?? null;
  React.useEffect(() => {
    const timer = setTimeout(
      () =>
        saveState({
          view: view ?? undefined,
          mode,
          route: routeState ?? undefined,
          place: clickedOrigin ?? undefined,
        }),
      300,
    );
    return () => clearTimeout(timer);
  }, [view, mode, routeState, clickedOrigin]);

  // подсказка из ссылки - открываем заново, адрес ищется как при клике
  const restoredPlaceRef = React.useRef(false);
  React.useEffect(() => {
    if (restoredPlaceRef.current) return;
    restoredPlaceRef.current = true;
    const place = initial.place;
    if (!place || (initial.mode ?? 'search') !== 'search') return;
    // улицу по ссылке не восстановить (нужно её название из тайлов) - как "другое"
    handleMapClick(place.lat, place.lon, place.kind === 'building' ? { kind: 'building' } : { kind: 'other' });
  }, [initial, handleMapClick]);

  return (
    <ThemeProvider theme={colorTheme === 'dark' ? darkTheme : lightTheme}>
      <GlobalStyles />
      <div style={{ position: 'fixed', inset: 0 }}>
        <MapView
          ref={mapRef}
          styleUrl={colorTheme === 'dark' ? config.darkStyleUrl : config.tileStyleUrl}
          dark={colorTheme === 'dark'}
          initialView={
            initial.view
              ? { lat: initial.view.lat, lng: initial.view.lon, zoom: initial.view.zoom, bearing: initial.view.bearing, pitch: initial.view.pitch }
              : config.initialView
          }
          onMapClick={handleMapClick}
          onMoveEnd={handleMapMoveEnd}
          pickMode={isPicking}
          popup={
            clicked && {
              lat: clicked.lat,
              lon: clicked.lon,
              content: (
                <PlacePopup
                  place={clicked.place}
                  onRoute={place => handleRouteTo(place.endpoint, place.routeLabel ?? place.title)}
                />
              ),
            }
          }
          onPopupClose={handlePopupClose}
        />

        <MapControls
          onZoomIn={() => mapRef.current?.zoomIn()}
          onZoomOut={() => mapRef.current?.zoomOut()}
          onLocate={() => mapRef.current?.locate()}
          dark={colorTheme === 'dark'}
          onThemeToggle={toggleTheme}
        />

        <SidePanel className="overlay-panel">
          <Toolbar mode={mode} onModeChange={handleModeChange} />

          {mode === 'search' ? (
            <SearchBox onSelect={handleSelect} near={mapCenter} />
          ) : (
            <RoutePanel
              near={mapCenter}
              onRouteChange={handleRouteChange}
              preset={routePreset}
              pick={pick}
              onPickToggle={handlePickToggle}
              initial={routeState}
              onStateChange={setRouteState}
            />
          )}
        </SidePanel>

        {mode === 'search' && selected && (
          <AddressCard
            result={selected}
            canGiveFeedback={selectedLogId != null}
            feedback={feedback}
            onRoute={() => handleRouteTo(selected, selected.fullAddress)}
            onConfirm={handleConfirm}
            onNoGeo={handleNoGeo}
          />
        )}
      </div>
    </ThemeProvider>
  );
};

export default App;
